/**
 * Figma Plugin import library API (DB-backed prototype list).
 */

import { apiFetch } from "./base";
import type { BulkDeleteResponse } from "./library";
import {
  assessThumbnailQuality,
  getThumbnailQualityLabel,
  type ThumbnailQualityStatus,
} from "./thumbnailQuality";

export type FigmaPluginThumbnailStatus =
  | "available"
  | "missing"
  | "stale"
  | "expired"
  | "error";

export interface FigmaPluginImport {
  id: string;
  raw_url: string;
  normalized_url: string;
  file_key: string;
  node_id: string | null;
  file_name: string | null;
  node_name: string | null;
  display_name?: string | null;
  thumbnail_url: string | null;
  thumbnail_source?: "figma" | "manual" | null;
  width: number | null;
  height: number | null;
  thumbnail_pixel_width?: number | null;
  thumbnail_pixel_height?: number | null;
  thumbnail_scale?: number | null;
  thumbnail_quality_status?: ThumbnailQualityStatus | null;
  thumbnail_resolved_at: string | null;
  thumbnail_expires_at: string | null;
  thumbnail_refresh_after_at: string | null;
  thumbnail_status: FigmaPluginThumbnailStatus;
  created_at: string;
  updated_at: string;
}

interface FigmaPluginImportResponse {
  status: string;
  data: FigmaPluginImport;
}

interface FigmaPluginImportListResponse {
  status: string;
  items: FigmaPluginImport[];
}

export function getFigmaPluginImportDisplayName(item: FigmaPluginImport): string {
  const override = item.display_name?.trim();
  if (override) return override;
  const f = item.file_name?.trim();
  const n = item.node_name?.trim();
  if (f && n) return `${f} / ${n}`;
  return f || n || item.normalized_url;
}

export function getFigmaPluginImportSizeLabel(item: FigmaPluginImport): string {
  if (item.width && item.height) {
    return `${item.width} × ${item.height} px`;
  }
  return "—";
}

export function getFigmaPluginImportSizeValueLabel(
  item: FigmaPluginImport,
): string {
  if (item.width && item.height) {
    return `${item.width} × ${item.height}`;
  }
  return "—";
}

const PIXELS_PER_INCH = 72;
const MILLIMETERS_PER_INCH = 25.4;

export function getFigmaPluginImportPhysicalSizeLabel(
  item: FigmaPluginImport,
): string | null {
  const physicalSize = getFigmaPluginImportPhysicalSizeMm(item);
  if (!physicalSize) {
    return null;
  }
  return `${physicalSize.width.toFixed(1)} × ${physicalSize.height.toFixed(1)} mm`;
}

export function getFigmaPluginImportPhysicalSizeMm(
  item: FigmaPluginImport,
): { width: number; height: number } | null {
  if (!item.width || !item.height) {
    return null;
  }
  return {
    width: (item.width * MILLIMETERS_PER_INCH) / PIXELS_PER_INCH,
    height: (item.height * MILLIMETERS_PER_INCH) / PIXELS_PER_INCH,
  };
}

export function getFigmaPluginImportPhysicalSizeValueLabel(
  item: FigmaPluginImport,
): string {
  const physicalSizeLabel = getFigmaPluginImportPhysicalSizeLabel(item);
  return physicalSizeLabel?.replace(" mm", "") ?? "—";
}

export function getFigmaPluginImportDimensionLabel(
  item: FigmaPluginImport,
): string {
  const sizeLabel = getFigmaPluginImportSizeLabel(item);
  const physicalSizeLabel = getFigmaPluginImportPhysicalSizeLabel(item);
  return physicalSizeLabel ? `${sizeLabel} · ${physicalSizeLabel}` : sizeLabel;
}

export function getFigmaPluginImportThumbnailPixelSizeValueLabel(
  item: FigmaPluginImport,
): string {
  if (!item.thumbnail_pixel_width || !item.thumbnail_pixel_height) return "—";
  return `${item.thumbnail_pixel_width} × ${item.thumbnail_pixel_height}`;
}

export function getFigmaPluginImportThumbnailQualityLabel(
  item: FigmaPluginImport,
): string {
  const derived = assessThumbnailQuality(
    item.width,
    item.height,
    item.thumbnail_pixel_width,
    item.thumbnail_pixel_height,
    item.thumbnail_quality_status === "capped",
  );
  return getThumbnailQualityLabel(item.thumbnail_quality_status ?? derived.status);
}

type FigmaPluginImportDisplayFields = FigmaPluginImport & {
  affectedSpacesCount?: number | null;
  affected_spaces_count?: number | null;
  storageBytes?: number | null;
  storageLabel?: string | null;
  storage_bytes?: number | null;
  storage_label?: string | null;
  usedInCount?: number | null;
  used_in_count?: number | null;
  view_r_storage?: string | null;
  view_r_storage_label?: string | null;
};

function readFiniteCount(...values: Array<number | null | undefined>): number {
  const value = values.find(
    (candidate): candidate is number =>
      typeof candidate === "number" && Number.isFinite(candidate),
  );
  return value == null ? 0 : Math.max(0, Math.floor(value));
}

function formatStorageBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const precision = value >= 10 || unitIndex === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
}

export function getFigmaPluginImportStorageLabel(
  item: FigmaPluginImport,
): string {
  const displayItem = item as FigmaPluginImportDisplayFields;
  const explicitLabel =
    displayItem.view_r_storage_label ??
    displayItem.view_r_storage ??
    displayItem.storageLabel ??
    displayItem.storage_label;
  if (explicitLabel?.trim()) {
    return explicitLabel.trim();
  }

  const bytes = displayItem.storageBytes ?? displayItem.storage_bytes;
  if (typeof bytes === "number" && Number.isFinite(bytes)) {
    return formatStorageBytes(bytes);
  }

  return "0 GB";
}

export function getFigmaPluginImportUsedInCount(
  item: FigmaPluginImport,
): number {
  const displayItem = item as FigmaPluginImportDisplayFields;
  return readFiniteCount(displayItem.usedInCount, displayItem.used_in_count);
}

export function getFigmaPluginImportUsedInLabel(
  item: FigmaPluginImport,
): string {
  return String(getFigmaPluginImportUsedInCount(item));
}

export function getFigmaPluginImportAffectedSpacesCount(
  items: FigmaPluginImport[],
): number {
  return items.reduce((total, item) => {
    const displayItem = item as FigmaPluginImportDisplayFields;
    return total + readFiniteCount(
      displayItem.affectedSpacesCount,
      displayItem.affected_spaces_count,
    );
  }, 0);
}

export function getFigmaPluginImportAffectedSpacesLabel(
  items: FigmaPluginImport[],
): string {
  const count = getFigmaPluginImportAffectedSpacesCount(items);
  return `${count} space${count === 1 ? "" : "s"} affected`;
}

export function needsFigmaPluginThumbnailRefresh(
  item: FigmaPluginImport,
): boolean {
  return (
    item.thumbnail_status === "stale" ||
    item.thumbnail_status === "expired" ||
    item.thumbnail_status === "error"
  );
}

export async function listFigmaPluginImports(): Promise<FigmaPluginImport[]> {
  const res = await apiFetch("/api/figma/plugin-imports");
  if (!res.ok) {
    throw new Error(`Failed to list Figma imports (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportListResponse;
  return body.items ?? [];
}

export async function resolveFigmaPluginImport(
  url: string,
  options?: { bypassCache?: boolean },
): Promise<FigmaPluginImport> {
  const res = await apiFetch("/api/figma/plugin-imports/resolve", {
    method: "POST",
    body: JSON.stringify({
      url,
      bypass_cache: Boolean(options?.bypassCache),
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Resolve failed (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportResponse;
  return body.data;
}

export async function refreshFigmaPluginImport(
  importId: string,
): Promise<FigmaPluginImport> {
  const res = await apiFetch(
    `/api/figma/plugin-imports/${encodeURIComponent(importId)}/refresh`,
    { method: "POST" },
  );
  if (!res.ok) {
    throw new Error(`Refresh failed (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportResponse;
  return body.data;
}

export async function updateFigmaPluginImportDisplayName(
  importId: string,
  displayName: string,
): Promise<FigmaPluginImport> {
  const res = await apiFetch(
    `/api/figma/plugin-imports/${encodeURIComponent(importId)}`,
    { method: "PATCH", body: JSON.stringify({ display_name: displayName }) },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Rename failed (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportResponse;
  return body.data;
}

export async function updateFigmaPluginImportThumbnail(
  importId: string,
  imageData: string,
): Promise<FigmaPluginImport> {
  const res = await apiFetch(
    `/api/figma/plugin-imports/${encodeURIComponent(importId)}/thumbnail`,
    { method: "POST", body: JSON.stringify({ image_data: imageData }) },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Thumbnail upload failed (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportResponse;
  return body.data;
}

export async function deleteFigmaPluginImport(importId: string): Promise<void> {
  const res = await apiFetch(
    `/api/figma/plugin-imports/${encodeURIComponent(importId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    throw new Error(`Delete failed (${res.status})`);
  }
}

export async function bulkDeleteFigmaPluginImports(
  ids: string[],
): Promise<BulkDeleteResponse> {
  const res = await apiFetch("/api/figma/plugin-imports/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    throw new Error(`Bulk delete failed (${res.status})`);
  }
  return (await res.json()) as BulkDeleteResponse;
}

export async function reloadFigmaPluginImportList(): Promise<FigmaPluginImport[]> {
  return listFigmaPluginImports();
}

/** @deprecated Use reloadFigmaPluginImportList — refresh all means reload the user's list from API. */
export async function refreshAllFigmaPluginImports(
  _items: FigmaPluginImport[],
): Promise<FigmaPluginImport[]> {
  return reloadFigmaPluginImportList();
}

export async function getFigmaPluginImport(
  importId: string,
): Promise<FigmaPluginImport> {
  const res = await apiFetch(
    `/api/figma/plugin-imports/${encodeURIComponent(importId)}`,
  );
  if (!res.ok) {
    throw new Error(`Import not found (${res.status})`);
  }
  const body = (await res.json()) as FigmaPluginImportResponse;
  return body.data;
}
